import React, { FC, useEffect, useMemo } from "react"
import { InfiniteLoader } from "dash-styled"
import { LabelingLayout } from "../../../../../../Common/LabelingLayout/LabelingLayout"
import { APIAppInstanceModel } from "../../../../../../../Models/API/AppInstanceModel"
import { Result } from "../../../../../../../Models/Utility/Result"
import { AppDetailLink } from "../../../../../../Common/AppDetailLink/AppDetailLink"
import { useAssumedRoles } from "../../../../../../../Hooks/useAssumedRoles"
import { useDebounce } from "../../../../../../../Hooks/useDebounce"

export interface PrepareLabelingProps {
    orgUsername: string
    onBack: () => void
    onForward?: () => void
    instance?: APIAppInstanceModel
    jobID?: string
    openLabelingUI: (orgUsername: string, instanceID: string) => void
    killOnboardPolling: () => void
    templateTitle: string
}

export const PrepareLabeling: FC<PrepareLabelingProps> = ({
    orgUsername,
    onBack,
    instance,
    jobID,
    openLabelingUI,
    killOnboardPolling,
    templateTitle
}) => {
    // fetch when available
    useAssumedRoles(instance?.uuid)

    useEffect(() => {
        if (!!instance && !!jobID) {
            killOnboardPolling()
            openLabelingUI(orgUsername, instance.uuid)
        }
    }, [instance, jobID, killOnboardPolling, openLabelingUI, orgUsername])

    const showNextJobLabel = useDebounce(instance, 4000)

    const preparationStageText = useMemo(
        () =>
            instance
                ? jobID
                    ? "Building annotation interface"
                    : !!showNextJobLabel
                    ? "Queuing your data for processing"
                    : "Adding your data"
                : "Creating your project",
        [instance, jobID, showNextJobLabel]
    )

    return (
        <LabelingLayout
            orgUsername={orgUsername}
            mode="onboarding_labeling"
            instanceFetch={instance ? Result.ok(instance) : undefined}
            onBack={onBack}
            toolbar={<div />}
            title={templateTitle}
        >
            <div
                className="flex flex-col h-full justify-center items-center bg-rhino-100 relative"
                data-testid="prepare-labeling"
            >
                <InfiniteLoader />
                <p className="mt-4 text-rhino-900 font-semibold">
                    Setting everything up for processing. Won’t be long
                </p>
                <p
                    className="mt-2 text-rhino-900 opacity-75"
                    data-testid="prepare-labeling-loading-text"
                >
                    {preparationStageText}
                </p>
                <AppDetailLink
                    className="absolute bottom-0 mb-20"
                    orgUsername={orgUsername}
                    appID={instance?.uuid}
                />
            </div>
        </LabelingLayout>
    )
}
